<template>
  <AdminLayout>
    <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6">
        <router-link to="/audit" class="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-3">
          <ArrowLeft class="w-4 h-4" /> Retour aux logs d'audit
        </router-link>
        <div class="flex items-center gap-3">
          <div class="p-2.5 bg-indigo-50 rounded-xl">
            <Mail class="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-dashboard-text">Timeline — Courrier #{{ courrierId }}</h1>
            <p class="text-sm text-dashboard-muted mt-0.5">
              Toutes les actions enregistrées sur ce courrier, classées chronologiquement.
            </p>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="bg-white border border-dashboard-border rounded-xl p-16 text-center">
        <div class="w-10 h-10 border-4 border-[#22282F]/30 border-t-[#22282F] rounded-full animate-spin mx-auto mb-4" />
        <p class="text-dashboard-muted">Chargement…</p>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle class="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <p class="font-medium text-red-800">Erreur</p>
          <p class="text-sm text-red-600 mt-1">{{ error }}</p>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!timeline.length" class="bg-white border border-dashboard-border rounded-xl p-12 text-center">
        <History class="w-12 h-12 text-dashboard-muted mx-auto mb-3" />
        <p class="text-dashboard-muted">Aucune action enregistrée pour ce courrier.</p>
      </div>

      <!-- Stats -->
      <div v-else class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Actions totales" :value="String(timeline.length)" :icon="Activity" tone="slate" />
        <StatCard label="Utilisateurs distincts" :value="String(uniqueUsers)" :icon="Users" tone="indigo" />
        <StatCard label="Actions critiques" :value="String(criticalCount)" :icon="ShieldAlert" tone="red" />
        <StatCard label="Sessions corrélées" :value="String(uniqueRequestIds)" :icon="LinkIcon" tone="blue" />
      </div>

      <!-- Timeline grouped by request_id -->
      <div v-if="timeline.length" class="space-y-3">
        <div
          v-for="group in groupedEntries"
          :key="group.key"
          class="bg-white border border-dashboard-border rounded-xl overflow-hidden"
        >
          <!-- Group header (request_id) -->
          <div class="border-b border-dashboard-border bg-dashboard-bg px-4 py-2 flex items-center gap-2 text-xs">
            <LinkIcon class="w-3.5 h-3.5 text-dashboard-muted" />
            <span class="text-dashboard-muted">Session / requête</span>
            <code v-if="group.requestId" class="font-mono text-dashboard-text">{{ group.requestId }}</code>
            <span v-else class="text-dashboard-muted italic">— sans request_id —</span>
            <span class="text-dashboard-muted ml-2">·</span>
            <span class="text-dashboard-muted">{{ group.entries.length }} action(s)</span>
            <router-link
              v-if="group.requestId"
              :to="`/audit/request/${group.requestId}`"
              class="text-blue-600 hover:underline ml-auto"
            >Vue corrélation ›</router-link>
          </div>

          <!-- Entries -->
          <div class="divide-y divide-dashboard-border">
            <button
              v-for="entry in group.entries"
              :key="entry.id"
              @click="selectedLog = entry"
              :class="[
                'w-full text-left px-4 py-3 hover:bg-dashboard-bg transition-colors flex items-start gap-3 border-l-4',
                SEVERITY_META[entry.severity].ring,
              ]"
            >
              <component
                :is="getDomainMeta(entry.action).icon"
                class="w-4 h-4 mt-0.5 text-dashboard-muted flex-shrink-0"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-medium text-dashboard-text">{{ getActionLabel(entry.action) }}</span>
                  <span v-if="entry.severity !== 'info'" :class="['text-[10px] px-1.5 py-0.5 rounded font-medium', SEVERITY_META[entry.severity].badge]">
                    {{ SEVERITY_META[entry.severity].label }}
                  </span>
                </div>
                <div class="text-xs text-dashboard-muted mt-0.5 flex items-center gap-2 flex-wrap">
                  <template v-if="entry.user">
                    par <router-link :to="`/audit/user/${entry.user.id}`" @click.stop class="text-blue-600 hover:underline">{{ entry.user.full_name }}</router-link>
                  </template>
                  <template v-else>par <span class="italic">système</span></template>
                  <span>·</span>
                  <span>{{ formatDateTime(entry.created_at) }}</span>
                  <span v-if="entry.ip_address">·</span>
                  <span v-if="entry.ip_address" class="font-mono">{{ entry.ip_address }}</span>
                </div>
              </div>
              <ChevronRight class="w-4 h-4 text-dashboard-muted flex-shrink-0 mt-1" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <AuditLogDetailPanel
      v-if="selectedLog"
      :log="selectedLog"
      @close="selectedLog = null"
    />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowLeft, Mail, History, AlertCircle, Activity, Users, ShieldAlert,
  Link as LinkIcon, ChevronRight,
} from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuditLogDetailPanel from '@/components/audit/AuditLogDetailPanel.vue'
import StatCard from '@/components/audit/StatCard.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'
import {
  SEVERITY_META, getActionLabel, getDomainMeta, formatDateTime,
} from '@/utils/auditMeta'
import type { AuditLogEntry } from '@/utils/auditMeta'

const route = useRoute()
const toast = useToast()

const courrierId = computed(() => Number(route.params.id))
const timeline = ref<AuditLogEntry[]>([])
const loading = ref(false)
const error = ref('')
const selectedLog = ref<AuditLogEntry | null>(null)

const uniqueUsers = computed(() =>
  new Set(timeline.value.filter(e => e.user).map(e => e.user!.id)).size,
)
const criticalCount = computed(() =>
  timeline.value.filter(e => e.severity === 'critical').length,
)
const uniqueRequestIds = computed(() =>
  new Set(timeline.value.filter(e => e.request_id).map(e => e.request_id!)).size,
)

const groupedEntries = computed(() => {
  const groups: Array<{ key: string; requestId: string | null; entries: AuditLogEntry[] }> = []
  const seen = new Map<string, number>()
  for (const entry of timeline.value) {
    const key = entry.request_id || `_no_req_${entry.id}`
    if (seen.has(key)) {
      groups[seen.get(key)!].entries.push(entry)
    } else {
      seen.set(key, groups.length)
      groups.push({ key, requestId: entry.request_id || null, entries: [entry] })
    }
  }
  return groups
})

async function fetchTimeline() {
  loading.value = true
  error.value = ''
  try {
    const result = await apiFetch(`/api/admin/audit/courrier/${courrierId.value}/`)
    timeline.value = result.timeline || []
  } catch (e: any) {
    error.value = e?.message || 'Erreur'
    toast.error('Erreur', error.value)
  } finally {
    loading.value = false
  }
}

watch(courrierId, fetchTimeline)
onMounted(fetchTimeline)
</script>
